import {
  CopyIcon,
  EllipsisVerticalIcon,
  Loader2,
  TrashIcon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "../ui/button";
import { useDuplicateProject } from "@/hooks/use-duplicate-project";
import { useDeleteProject } from "@/hooks/use-delete-project";
import { PencilSquareIcon } from "@heroicons/react/16/solid";
import { RenameProjectDialog } from "./rename-project-dialog";
import type { GetProjectsResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { useState } from "react";

export function ProjectOptionsDropdown({
  project,
}: {
  project: GetProjectsResponseBody["projects"][number];
}) {
  const [open, setOpen] = useState(false);

  const { mutate: duplicateProject, isPending: isDuplicating } =
    useDuplicateProject({
      onSuccess: () => setOpen(false),
    });
  const { mutate: deleteProject, isPending: isDeleting } = useDeleteProject({
    onSuccess: () => setOpen(false),
  });

  const disableAll = isDuplicating || isDeleting;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-6 w-6">
          <EllipsisVerticalIcon className="size-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            duplicateProject({ projectId: project.id });
          }}
          disabled={isDuplicating || disableAll}
        >
          {isDuplicating ? (
            <Loader2 className="size-4 animate-spin inline" />
          ) : (
            <CopyIcon className="size-4" />
          )}
          {isDuplicating ? "Duplicating" : "Duplicate"}
        </DropdownMenuItem>
        <RenameProjectDialog
          projectId={project.id}
          currentName={project.name}
          onOpenChange={(dialogOpen) => {
            if (!dialogOpen) {
              setOpen(false);
            }
          }}
        >
          <DropdownMenuItem
            disabled={isDeleting || disableAll}
            onSelect={(e) => e.preventDefault()}
          >
            <PencilSquareIcon className="size-4" />
            Rename
          </DropdownMenuItem>
        </RenameProjectDialog>
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteProject({ projectId: project.id });
          }}
          disabled={isDeleting || disableAll}
        >
          {isDeleting ? (
            <Loader2 className="size-4 animate-spin inline" />
          ) : (
            <TrashIcon className="size-4" />
          )}
          {isDeleting ? "Deleting" : "Delete"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
